import { AurigaSettings } from 'types/Auth.types';

/**
 * Gets the value of a cookie by name
 * @param cookieName
 */
const getCookieValue = (cookieName: string): string | undefined => {
  const cookies = document.cookie.split(';');
  const cookieEntry = cookies.find((cookie) => cookie.trim().startsWith(`${cookieName}=`));

  if (!cookieEntry) {
    return undefined;
  }

  // Determines the first '=' text occurrence in the cookie string
  const firstEqualPos = cookieEntry.indexOf('=');
  if (firstEqualPos === -1) {
    return undefined;
  }

  // Return the value part of the cookie (i.e. everything after the first '=')
  return cookieEntry.substring(firstEqualPos + 1);
};

export default getCookieValue;

/**
 * Retrieves Auriga application settings from cookies
 *
 * These settings include environment-specific configuration such as:
 * - API endpoints for AppSync GraphQL
 * - API keys
 * - Region information
 * - Authentication types
 *
 * The settings are base64 encoded in the cookie for security.
 *
 * @returns The decoded and parsed Auriga settings object
 * @throws Error if the cookie is not found or cannot be parsed
 */
export const getAurigaSettings = (): AurigaSettings => {
  // Get the auriga-settings cookie
  const aurigaSettingsCookieValue = getCookieValue('auriga-settings');

  if (!aurigaSettingsCookieValue) {
    // No cookie found - this is an error in all stages
    throw new Error('Auriga settings cookie not found. Make sure the Auriga settings cookie is being set correctly.');
  }

  try {
    // First unescape the special characters
    const unescapedAurigaSettings = aurigaSettingsCookieValue
      .replace(/\\"/g, '"') // Unescape quotes
      .replace(/\\n/g, '\n'); // Unescape newlines

    // Decode the base64 encoded value and parse the JSON
    const decodedAurigaSettings = atob(unescapedAurigaSettings);

    return JSON.parse(decodedAurigaSettings);
  } catch (error) {
    console.error('Failed to parse Auriga settings cookie:', error);
    throw error;
  }
};
